import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Grid,
} from "@mui/material";
import { formatDecimal } from "../../utils/numberFormat";

export default function MorbidityResultsWalkersCyclists({ projects, forPdf=false }) {
  /* ----------------------------------------------------
   * CORES
   * -------------------------------------------------- */
  const CONDITION_COLORS = [
    "#CFE6EF", // Coronary heart disease
    "#BDEBD9", // Stroke
    "#F3D7A1", // Dementia
    "#EDD1C6", // Type II diabetes
    "#E9DDF3", // Depression
    "#E04A8B", // Breast cancer (female)
    "#12C7C3", // Colon cancer (male)
  ];
  const colorForCondition = (i) => CONDITION_COLORS[i % CONDITION_COLORS.length];

  // (Opcional) cor para título do projeto (se quiseres dar destaque por projeto)
  const PROJECT_PALETTE = ["#7C3AED", "#22C55E", "#3B82F6"];
  const colorForProject = (i) => PROJECT_PALETTE[i % PROJECT_PALETTE.length];

  /* ----------------------------------------------------
   * MÉTRICAS (CONDIÇÕES)
   * -------------------------------------------------- */
  // Cada métrica devolve percentagem (0–100). valueFn lida com nomes diferentes.
  const METRICS = [
    {
      key: "chd",
      label: "Coronary heart disease",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.coronaryHeartReductionInRiskOfDisease ??
          p?.projectInfo?.coronaryHeartReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "stroke",
      label: "Stroke",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.strokeReductionInRiskOfDisease ??
          p?.projectInfo?.strokeReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "dementia",
      label: "Dementia",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.dementiaReductionInRiskOfDisease ??
          p?.projectInfo?.dementiaReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "t2d",
      label: "Type II diabetes",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.typeIIDiabetesReductionInRiskOfDisease ??
          p?.projectInfo?.typeIIDiabetesReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "depression",
      label: "Depression",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.depressionReductionInRiskOfDisease ??
          p?.projectInfo?.depressionReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "breastCancerF",
      label: "Breast cancer (female)",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.breastCancerReductionInRiskOfDisease ??
          p?.projectInfo?.breastCancerReductionInRiskDisease ??
          0) * 100,
    },
    {
      key: "colonCancerM",
      label: "Colon cancer (male)",
      unit: "%",
      decimals: 2,
      valueFn: (p) =>
        (p?.projectInfo?.colonCancerReductionInRiskOfDisease ??
          p?.projectInfo?.colonCancerReductionInRiskDisease ??
          0) * 100,
    },
  ];

  /* ----------------------------------------------------
   * HELPERS
   * -------------------------------------------------- */
  function formatPercent(value, decimals = 2) {
    if (typeof value !== "number" || !Number.isFinite(value)) return "--";
    return formatDecimal(value, decimals);
  }

  // Obtém o valor (em %) para uma métrica e projeto
  function getMetricValue(project, metric) {
    if (typeof metric.valueFn === "function") return metric.valueFn(project);
    return 0;
  }

  // Para a barra empilhada: normaliza cada segmento por projeto
  function getStackParts(project) {
    const parts = METRICS.map((m, i) => ({
      key: m.key,
      label: m.label,
      value: Math.max(0, getMetricValue(project, m)), // clamp a 0
      color: colorForCondition(i),
      unit: m.unit,
      decimals: m.decimals,
    }));
    const total = parts.reduce((s, p) => s + p.value, 0);
    return {
      total,
      parts: parts.map((p) => ({
        ...p,
        widthPct: total > 0 ? (p.value / total) * 100 : 0,
      })),
    };
  }

  /* ----------------------------------------------------
   * RENDER
   * -------------------------------------------------- */
  return (
    <Box>
      <Typography
        variant="h6"
        align="center"
        sx={{ fontWeight: 700, mb: 3, mt: 1, ...(forPdf && { mt: 0, mb: 1 }) }}
      >
        Reduction in risk of disease for new walkers and cyclists
      </Typography>

      {/* --- Tabela --- */}
      <TableContainer sx={{ width: "100%" }}>
        <Table stickyHeader sx={{ mt: 1.5, tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{
                "& > .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0 !important",
                },
              }}
            >
              <TableCell sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="left"
                  sx={{ fontWeight: 600, px: 0 }}
                >
                  Condition
                </Typography>
              </TableCell>

              {/* uma coluna por projeto */}
              {projects.map((project) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{ width: 160, maxWidth: 160, px: 0 }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="center"
                    sx={{ fontWeight: 600, px: 1 }}
                  >
                    {project.projectName}
                  </Typography>
                </TableCell>
              ))}

              <TableCell sx={{ width: 80, maxWidth: 80 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                  sx={{ fontWeight: 600, px: 1 }}
                >
                  Unit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {METRICS.map((m, idxCond) => (
              <TableRow key={m.key} aria-label={m.key}>
                {/* Condição */}
                <TableCell
                  sx={{
                    width: `calc(100% - ${projects.length * 160}px)`,
                    py: 1.25,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.label}
                  </Typography>
                </TableCell>

                {/* Valores por projeto (em %) */}
                {projects.map((project, idxProj) => {
                  const val = getMetricValue(project, m);
                  const isNumber =
                    typeof val === "number" && Number.isFinite(val);
                  return (
                    <TableCell
                      key={(project.id ?? project.projectName) + ":" + m.key}
                      align="right"
                      sx={{
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        borderBottom: "1px solid #e0e0e0 !important",
                      }}
                    >
                      <Typography
                        variant="body2"
                        align="center"
                        noWrap
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          //color: colorForProject(idxProj), // opcional
                        }}
                        title={isNumber ? String(val) : ""}
                      >
                        {isNumber ? formatPercent(val, m.decimals) : "--"}
                      </Typography>
                    </TableCell>
                  );
                })}

                {/* Unidade */}
                <TableCell
                  sx={{
                    width: 80,
                    maxWidth: 80,
                    px: 0,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" align="center" noWrap>
                    {m.unit}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- Legenda de cores por condição --- */}
      <Box sx={{ mt: 10, mb: 4,...(forPdf && { mt: 2, mb: 0 }) }}>
        <Stack direction="row" spacing={0.2} alignItems="flex-start" flexWrap="wrap">
          {METRICS.map((m, i) => (
            <Stack
              key={"legend:" + m.key}
              direction="column"
              spacing={0.5}
              alignItems="center"
              sx={{ minWidth: 80 }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 0,
                  backgroundColor: colorForCondition(i),
                }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ textAlign: "center", whiteSpace: "nowrap", px:1.5 }}
              >
                {m.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* --- Barra empilhada por projeto --- */}
      <Box sx={{ width: "100%", mt: 3, ...(forPdf && { mt: 1, mb:0 }) }}>
        <Grid container spacing={forPdf ? 0.55 : 3}>
          {(() => {
            // 1) Calcular o maior total entre os projetos (sem sair do bloco)
            const totals = projects.map((proj) => getStackParts(proj).total || 0);
            const maxTotal = Math.max(0, ...totals);

            // 2) Renderizar cada projeto com barra de largura proporcional ao seu total
            return projects.map((project, idxProj) => {
              const { total, parts } = getStackParts(project);
              const projectColor = colorForProject(idxProj);

              // Largura proporcional (0..100). Se maxTotal=0, fica 0.
              const proportionalWidthPct =
                maxTotal > 0 ? (total / maxTotal) * 100 : 0;

              // Mínimo visual para não “desaparecer” (ajusta/remoção conforme preferires)
              const barWidthPct = Math.min(Math.max(proportionalWidthPct, 6), 100);

              return (
                <Grid
                  item
                  xs={12}
                  key={"stack:" + (project.id ?? project.projectName)}
                >
                  {/* Nome do projeto */}
                  <Typography
                    variant="subtitle1"
                    sx={{ color: projectColor, mb: 0, lineHeight: 1.4 }}
                  >
                    {project.projectName}
                  </Typography>

                  {/* Barra empilhada (mt:0 e sem gaps verticais) */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0}
                    sx={{ width: "100%", mt: 0 }}
                  >
                    {/* Container da barra com largura proporcional ao total do projeto */}
                    <Box
                      sx={{
                        width: `${barWidthPct}%`,     // <-- largura proporcional ao total
                        height: 16,
                        borderRadius: 0,
                        overflow: "hidden",
                        display: "flex",
                        transition: "width .25s ease", // opcional: animação suave
                        // opcional: track leve, caso queiras igual ao mock
                        //backgroundColor: (theme) => theme.palette.action.hover,
                      }}
                      title={`Total: ${formatPercent(total, 2)}`}
                    >
                      {/* segmentos (mantém a lógica original dos parts) */}
                      {parts.map((p) => (
                        <Box
                          key={"seg:" + p.key}
                          sx={{
                            width: `${p.widthPct}%`,   // percentuais relativos ao total do projeto
                            backgroundColor: p.color,
                            height: "100%",
                            // opcional: visibilidade mínima por segmento
                            minWidth: 2,
                          }}
                          title={`${p.label}: ${formatPercent(p.value, p.decimals)} ${p.unit}`}
                        />
                      ))}
                    </Box>

                    {/* total (opcional) */}
                    {/* <Typography
                      variant="body2"
                      sx={{
                        ml: 1,
                        minWidth: 80,
                        textAlign: "left",
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                        mt: 0,
                      }}
                      title="Soma simples das reduções (visualização)"
                    >
                      {total > 0 ? formatPercent(total, 2) + " total" : "--"}
                    </Typography> */}
                  </Stack>
                </Grid>
              );
            });
          })()}
        </Grid>
      </Box>

    </Box>
  );
}
